/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Alert, Card, Col, Row, Skeleton, Space, Typography } from 'antd';
import { CaretDownFilled, CaretUpFilled, LineOutlined } from '@ant-design/icons';
import ChartLoader from '../Analytics/ChartLoader';

// Misc imports
import moment from 'moment';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

// Constant declarations
const baseClassName = 'chart-card';
const DATE_FORMAT = 'DD MMM YYYY';

const ChartCard = ({
  className,
  loading = false,
  title,
  icon,
  trend,
  total,
  footer,
  startDate,
  endDate,
  children,
  ...rest
}) => {
  const { t } = useTranslation();

  const renderContent = () => {
    return (
      <Row className="h-100">
        <Col xs={24}>
          <Typography.Title level={5} className="fw-bolder">
            {title}
          </Typography.Title>
        </Col>

        <Col xs={12}>
          <Row className="h-100 mt-10">
            <Col span={24} justify="center" align="middle">
              {
                loading ? (
                  <div>
                    <Space>
                      <Skeleton.Avatar active size={48} shape="circle" />
                      <Skeleton.Input active size={48} />
                    </Space>
                  </div>
                ) : (
                  <Typography.Title className="fw-800 mb-2 fs-xxxxl">
                    <Space>
                      <span className="text-muted">{icon}</span>
                      {formatNumberToThousandSeparator(total)}
                    </Space>
                  </Typography.Title>
                )
              }

              {
                loading ? (
                  <Skeleton.Input active size="large" className="mt-2" />
                ) : (
                  <Alert
                    className="border-0 mb-8 ant-alert-w-auto"
                    type={trend > 0 ? 'success' : trend < 0 ? 'warning' : 'info'}
                    message={
                      <Space
                        className={classNames('px-6', {
                          'text-success': trend > 0,
                          'text-warning': trend < 0
                        })}
                      >
                        {trend > 0 ? <CaretUpFilled className="fs-lg" /> : trend < 0 ? <CaretDownFilled className="fs-lg" /> : <LineOutlined className="fs-lg" />}
                        <span className="fw-bold fs-lg">{trend && formatNumberToThousandSeparator(Math.abs(trend))}%</span>
                        <span className="fs-sm">{t('Last 30 days')}</span>
                      </Space>
                    }
                  />
                )
              }
            </Col>

            {
              footer && !loading && (
                <Col span={24} className="pr-6">
                  {footer}
                </Col>
              )
            }
          </Row>
        </Col>

        <Col xs={12}>
          <Row>
            <Col span={24} style={{ height: 215 }}>
              {
                loading ? (
                  <ChartLoader height={180} />
                ) : children
              }
            </Col>

            {
              startDate && endDate && (
                <Col span={24}>
                  <Row justify="space-between">
                    <Col className="fs-sm">{moment(startDate).format(DATE_FORMAT)}</Col>
                    <Col className="fs-sm">{moment(endDate).format(DATE_FORMAT)}</Col>
                  </Row>
                </Col>
              )
            }
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <Card
      bordered={false}
      className={classNames(baseClassName, className)}
      bodyStyle={{ height: '100%' }}
      {...rest}
    >
      {renderContent()}
    </Card>
  );
};

ChartCard.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  trend: PropTypes.number,
  total: PropTypes.number,
  footer: PropTypes.node,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  children: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string
};

export default ChartCard;
